import { useEffect, RefObject } from 'react'

/**
 * Scrolls an element into view after `delay` ms, whenever `trigger` changes to a truthy value.
 * The page runs inside a tall, fixed-height XM3 iframe: when the content fits, the PARENT window
 * is the scroller, so scroll that instead.
 */
export default function useScrollIntoView(
  ref: RefObject<HTMLElement>,
  trigger: unknown,
  delay = 450
): void {
  useEffect(() => {
    if (!trigger) return undefined
    const timer = setTimeout(() => {
      const el = ref.current
      if (!el) return
      const iframeScrolls =
        document.documentElement.scrollHeight > window.innerHeight + 4
      if (iframeScrolls) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
      try {
        const frame = window.frameElement as HTMLElement | null
        if (frame && window.parent && window.parent !== window) {
          const parentDoc = window.parent.document
          const scroller = (parentDoc.scrollingElement ||
            parentDoc.body) as HTMLElement
          const elTopInFrame =
            el.getBoundingClientRect().top -
            document.documentElement.getBoundingClientRect().top
          const frameTopInParent =
            frame.getBoundingClientRect().top + scroller.scrollTop
          scroller.scrollTo({
            top: Math.max(0, frameTopInParent + elTopInFrame - 24),
            behavior: 'smooth',
          })
        } else {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      } catch (e) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, delay)
    return () => clearTimeout(timer)
  }, [ref, trigger, delay])
}
